<template>
  <div class="hs-grid">

    <!-- Calories & macros -->
    <div class="hs-card">
      <div class="hs-card__header">
        <span class="hs-card__label">Apports journaliers</span>
      </div>
      <div class="hs-macros">
        <div class="hs-macro">
          <span class="hs-macro__value">{{ proteinNeeds.toFixed(0) }}<span class="hs-macro__unit">g</span></span>
          <span class="hs-macro__name" style="color:#3b82f6">Protéines</span>
        </div>
        <div class="hs-macro-sep" />
        <div class="hs-macro">
          <span class="hs-macro__value">{{ fatNeeds.toFixed(0) }}<span class="hs-macro__unit">g</span></span>
          <span class="hs-macro__name" style="color:#f59e0b">Lipides</span>
        </div>
        <div class="hs-macro-sep" />
        <div class="hs-macro">
          <span class="hs-macro__value">{{ carbNeeds.toFixed(0) }}<span class="hs-macro__unit">g</span></span>
          <span class="hs-macro__name" style="color:#10b981">Glucides</span>
        </div>
        <div class="hs-macro-sep" />
        <div class="hs-macro">
          <span class="hs-macro__value">30<span class="hs-macro__unit">g</span></span>
          <span class="hs-macro__name" style="color:#a78bfa">Fibres</span>
        </div>
      </div>
    </div>

    <!-- IMC -->
    <div class="hs-card">
      <div class="hs-card__header">
        <span class="hs-card__label">IMC</span>
        <span class="hs-card__badge hs-card__badge--subtle">{{ bmi.toFixed(1) }} · {{ bmiStatus }}</span>
      </div>
      <div class="hs-imc-bar">
        <div class="hs-imc-zone" style="background:#ef4444;width:37%" />
        <div class="hs-imc-zone" style="background:#22c55e;width:13%" />
        <div class="hs-imc-zone" style="background:#eab308;width:10%" />
        <div class="hs-imc-zone" style="background:#f97316;width:10%" />
        <div class="hs-imc-zone" style="background:#ef4444;width:30%" />
        <div class="hs-imc-cursor" :style="{ left: bmiPercent + '%' }" />
      </div>
      <div class="hs-imc-legend">
        <span>Sous-poids</span>
        <span>Normal</span>
        <span>Surpoids</span>
        <span>Obésité</span>
      </div>
    </div>

    <!-- Dépenses énergétiques -->
    <div class="hs-card hs-card--full">
      <div class="hs-card__header">
        <span class="hs-card__label">Dépenses énergétiques</span>
      </div>
      <div class="hs-energy-layout">

        <!-- Gauche : 3 blocs empilés -->
        <div class="hs-energy-left">

          <!-- Calories visées -->
          <div class="hs-cal-card hs-cal-card--accent hs-cal-card--compact">
            <div class="hs-cal-card__top">
              <span class="hs-cal-card__desc">Calories visées</span>
              <div class="hs-tooltip-wrap">
                <v-icon size="12" class="hs-info-icon hs-info-icon--light">mdi-information-outline</v-icon>
                <span class="hs-tooltip">Apport calorique journalier recommandé selon votre objectif (perte, maintien ou prise de poids).</span>
              </div>
            </div>
            <div class="hs-cal-card__num">
              <span class="hs-cal-card__val">{{ targetCalories.toFixed(0) }}</span>
              <span class="hs-cal-card__unit">kcal</span>
            </div>
          </div>

          <!-- Besoins journaliers -->
          <div class="hs-cal-card hs-cal-card--compact">
            <div class="hs-cal-card__top">
              <span class="hs-cal-card__desc">Besoins journaliers</span>
              <div class="hs-tooltip-wrap">
                <v-icon size="12" class="hs-info-icon">mdi-information-outline</v-icon>
                <span class="hs-tooltip">Dépense énergétique totale tenant compte de votre niveau d'activité physique (métabolisme de base × NAP).</span>
              </div>
            </div>
            <div class="hs-cal-card__num">
              <span class="hs-cal-card__val">{{ dailyNeeds.toFixed(0) }}</span>
              <span class="hs-cal-card__unit">kcal</span>
            </div>
          </div>

          <!-- Métabolisme de base -->
          <div class="hs-cal-card hs-cal-card--compact">
            <div class="hs-cal-card__top">
              <span class="hs-cal-card__desc">Métabolisme de base</span>
              <div class="hs-tooltip-wrap">
                <v-icon size="12" class="hs-info-icon">mdi-information-outline</v-icon>
                <span class="hs-tooltip">Énergie minimale que votre corps dépense au repos pour assurer ses fonctions vitales (respiration, circulation, température).</span>
              </div>
            </div>
            <div class="hs-cal-card__num">
              <span class="hs-cal-card__val">{{ tmb.toFixed(0) }}</span>
              <span class="hs-cal-card__unit">kcal</span>
            </div>
          </div>

        </div>

        <!-- Droite : lignes détail -->
        <div class="hs-energy-right">
          <div class="hs-rows">
            <div class="hs-row">
              <div class="hs-row__label-wrap">
                <span class="hs-row__label">Ajustement par rapport aux besoins journaliers</span>
                <div class="hs-tooltip-wrap">
                  <v-icon size="11" class="hs-info-icon">mdi-information-outline</v-icon>
                  <span class="hs-tooltip hs-tooltip--left">Différence entre vos besoins journaliers et vos calories visées selon votre objectif de poids.</span>
                </div>
              </div>
              <span class="hs-row__val">{{ calorieAdjustmentLabel }}</span>
            </div>
            <div class="hs-row">
              <span class="hs-row__label">Poids actuel</span>
              <span class="hs-row__val">{{ formatWeight(actualWeight) }}</span>
            </div>
            <div class="hs-row">
              <span class="hs-row__label">Poids visé</span>
              <span class="hs-row__val">{{ formatWeight(displayTargetWeight) }}</span>
            </div>
            <div v-if="estimatedMonthsLabel" class="hs-row">
              <div class="hs-row__label-wrap">
                <span class="hs-row__label">Temps estimé</span>
                <div class="hs-tooltip-wrap">
                  <v-icon size="11" class="hs-info-icon">mdi-information-outline</v-icon>
                  <span class="hs-tooltip hs-tooltip--left">Estimation basée sur un ajustement de ≈ 1,5 kg par mois.</span>
                </div>
              </div>
              <span class="hs-row__val">{{ estimatedMonthsLabel }}</span>
            </div>
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import { computed } from "vue";
import { useAuthStore } from "@/store/auth_store";
import {
  getBaseMetabolicRate,
  getDailyNeeds,
  getTargetCalories,
  calculateBmiData,
  calculateMacros
} from "@/service/health_service";

export default {
  name: "HealthStatsBloc",
  setup() {
    const authStore = useAuthStore();
    const toNumber = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };

    const actualWeight = computed(() => toNumber(authStore.userInfos?.actualWeight));
    const targetWeight = computed(() => toNumber(authStore.userInfos?.targetWeight));
    const displayTargetWeight = computed(() => targetWeight.value ?? actualWeight.value);

    const weight = computed(() => toNumber(authStore.userInfos?.actualWeight) ?? 70);
    const height = computed(() => toNumber(authStore.userInfos?.actualSize) ?? 175);
    const age = computed(() => authStore.userInfos?.age || 30);
    const sex = computed(() => authStore.userInfos?.sex || "male");
    const activityLevel = computed(() => authStore.userInfos?.activityLevel || "sedentary");
    const weightObjective = computed(() => authStore.userInfos?.weightObjective || "maintainWeight");
    const nap = computed(() => authStore.userInfos?.NAP || 1.5);

    const bmiData = computed(() => calculateBmiData(height.value, weight.value));
    const bmi = computed(() => bmiData.value.bmi);
    const bmiStatus = computed(() => bmiData.value.status);
    const bmiPercent = computed(() => bmiData.value.percent);

    const tmb = computed(() => getBaseMetabolicRate(age.value, height.value, weight.value, sex.value));
    const dailyNeeds = computed(() => getDailyNeeds(tmb.value, nap.value));
    const targetCalories = computed(() => getTargetCalories(dailyNeeds.value, weightObjective.value));

    const macros = computed(() => calculateMacros(weight.value, activityLevel.value, weightObjective.value, targetCalories.value));
    const proteinNeeds = computed(() => macros.value.proteinNeeds);
    const fatNeeds = computed(() => macros.value.fatNeeds);
    const carbNeeds = computed(() => macros.value.carbNeeds);

    const calorieAdjustment = computed(() => {
      if (weightObjective.value === "gainWeight") return 380;
      if (weightObjective.value === "loseWeight") return -380;
      return 0;
    });
    const calorieAdjustmentLabel = computed(() => {
      if (calorieAdjustment.value === 0) return "0 kcal (maintien)";
      const sign = calorieAdjustment.value > 0 ? "+" : "";
      return `${sign}${calorieAdjustment.value} kcal (≈ 1,5 kg/mois)`;
    });
    const estimatedMonthsLabel = computed(() => {
      if (weightObjective.value === "maintainWeight") return "";
      if (actualWeight.value == null || targetWeight.value == null) return "";
      const delta = Math.abs(targetWeight.value - actualWeight.value);
      if (delta === 0) return "Objectif atteint.";
      return `≈ ${(delta / 1.5).toFixed(1).replace(".", ",")} mois`;
    });
    const formatWeight = (v) => v == null ? "—" : `${v.toFixed(1)} kg`;

    return {
      bmi, bmiStatus, bmiPercent,
      tmb, dailyNeeds, targetCalories,
      proteinNeeds, fatNeeds, carbNeeds,
      actualWeight, displayTargetWeight,
      calorieAdjustmentLabel, estimatedMonthsLabel, formatWeight,
    };
  },
};
</script>

<style scoped>
.hs-grid {
  display: grid;
  gap: 10px;
}

/* Card générique */
.hs-card {
  background: rgb(var(--v-theme-tertiary));
  border-radius: 14px;
  border: 0.5px solid rgba(var(--v-theme-text), 0.08);
  padding: 16px 18px;
}
.hs-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}
.hs-card__label {
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
}
.hs-card__badge {
  font-size: 12px;
  font-weight: 600;
  background: rgb(var(--v-theme-accent));
  color: #fff;
  border-radius: 999px;
  padding: 3px 10px;
}
.hs-card__badge--subtle {
  background: rgba(var(--v-theme-text), 0.07);
  color: rgba(var(--v-theme-text), 0.7);
}

/* Macros */
.hs-macros {
  display: flex;
  align-items: center;
  gap: 0;
}
.hs-macro {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}
.hs-macro__value {
  font-size: 22px;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
  line-height: 1;
}
.hs-macro__unit {
  font-size: 12px;
  font-weight: 400;
  color: rgba(var(--v-theme-text), 0.45);
  margin-left: 1px;
}
.hs-macro__name {
  font-size: 11px;
  font-weight: 500;
}
.hs-macro-sep {
  width: 1px;
  height: 32px;
  background: rgba(var(--v-theme-text), 0.08);
  flex-shrink: 0;
}

/* IMC */
.hs-imc-bar {
  position: relative;
  display: flex;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 8px;
}
.hs-imc-zone { height: 100%; }
.hs-imc-cursor {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 14px;
  height: 14px;
  background: rgb(var(--v-theme-tertiary));
  border: 2.5px solid rgb(var(--v-theme-text));
  border-radius: 999px;
}
.hs-imc-legend {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: rgba(var(--v-theme-text), 0.35);
}

/* Layout dépenses énergétiques */
.hs-card--full {
  grid-column: 1 / -1;
}
.hs-energy-layout {
  display: flex;
  gap: 12px;
  align-items: stretch;
}
.hs-energy-left {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
  width: 220px;
  align-self: stretch;
}
.hs-energy-right {
  flex: 1;
  min-width: 0;
  align-self: stretch;
}

/* Compact variant — equal height across 3 cards to match right column */
.hs-cal-card--compact {
  flex: 1;
  padding: 8px 12px;
}
.hs-cal-card--compact .hs-cal-card__val {
  font-size: 20px;
}
.hs-cal-card--compact .hs-cal-card__top {
  margin-bottom: 2px;
}

/* Cal card top (label + info icon) */
.hs-cal-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  margin-bottom: 6px;
}

/* Tooltip system */
.hs-tooltip-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}
.hs-info-icon {
  color: rgba(var(--v-theme-text), 0.3);
  cursor: default;
}
.hs-info-icon--light {
  color: rgba(255,255,255,0.5);
}
.hs-tooltip {
  display: none;
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #1A1615;
  color: #fff;
  font-size: 11px;
  line-height: 1.45;
  padding: 7px 10px;
  border-radius: 8px;
  width: 200px;
  z-index: 10;
  pointer-events: none;
  white-space: normal;
}
.hs-tooltip--left {
  left: auto;
  right: 0;
  transform: none;
}
.hs-tooltip-wrap:hover .hs-tooltip {
  display: block;
}

/* Row label + info icon */
.hs-row__label-wrap {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}

/* Calories cards */
.hs-cal-cards {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
}
.hs-cal-card__num {
  display: flex;
  align-items: baseline;
  gap: 3px;
  margin-top: 4px;
}
.hs-cal-card {
  flex: 1;
  border-radius: 12px;
  background: rgba(var(--v-theme-text), 0.04);
  border: 0.5px solid rgba(var(--v-theme-text), 0.08);
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.hs-cal-card--accent {
  background: rgb(var(--v-theme-accent));
  border-color: transparent;
}
.hs-cal-card__val {
  font-size: 26px;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
  line-height: 1;
}
.hs-cal-card--accent .hs-cal-card__val { color: #fff; }
.hs-cal-card__unit {
  font-size: 11px;
  color: rgba(var(--v-theme-text), 0.45);
  font-weight: 400;
}
.hs-cal-card--accent .hs-cal-card__unit { color: rgba(255,255,255,0.65); }
.hs-cal-card__desc {
  font-size: 11px;
  color: rgba(var(--v-theme-text), 0.5);
}
.hs-cal-card--accent .hs-cal-card__desc { color: rgba(255,255,255,0.75); }

/* Lignes d'info */
.hs-rows { display: flex; flex-direction: column; gap: 6px; height: 100%; }
.hs-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(var(--v-theme-text), 0.03);
  border: 0.5px solid rgba(var(--v-theme-text), 0.06);
  flex: 1;
}
.hs-row__label {
  font-size: 12px;
  color: rgba(var(--v-theme-text), 0.5);
}
.hs-row__val {
  font-size: 12px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
}

@media (min-width: 760px) {
  .hs-grid {
    grid-template-columns: 1fr 1fr;
  }
  .hs-card:last-child {
    grid-column: 1 / -1;
  }
}
</style>
